/**
 * LinkPlaybookModal Component
 *
 * Modal for browsing and linking existing playbooks to a strategy.
 * Supports multi-select, search filtering, and active/archived sections.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Eye, EyeOff, Search, Plus } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Checkbox,
  Alert,
} from '@enfomentis/ui';

// 3. Services & Types
import { playbookService } from '@/services/PlaybookService';
import type { Playbook } from '@/types/schema/Playbook';

/** Sort alphabetically by name (case-insensitive). Returns a new sorted array. */
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

/**
 * Props for LinkPlaybookModal
 */
export interface LinkPlaybookModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful link - receives array of linked playbook IDs */
  onLink: (playbookIds: string[]) => void;
  /** Callback when user clicks "Create New Playbook" from empty state */
  onCreateNew: () => void;
  /** Strategy name for display */
  strategyName: string;
  /** Current playbook IDs already linked to this strategy */
  currentPlaybookIds: string[];
}

/**
 * LinkPlaybookModal Component
 */
export const LinkPlaybookModal: React.FC<LinkPlaybookModalProps> = ({
  isOpen,
  onClose,
  onLink,
  onCreateNew,
  strategyName,
  currentPlaybookIds,
}) => {
  // Data state
  const [allPlaybooks, setAllPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * Load all playbooks
   */
  const loadPlaybooks = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const playbooks = await playbookService.getAll();
      setAllPlaybooks(playbooks);
    } catch (error) {
      console.error('Failed to load playbooks:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setShowArchived(false);
      loadPlaybooks();
    }
  }, [isOpen, loadPlaybooks]);

  // Filter out playbooks already linked to this strategy
  const linkablePlaybooks = useMemo(() => {
    const currentIdSet = new Set(currentPlaybookIds);
    return allPlaybooks.filter(p => !currentIdSet.has(p.id));
  }, [allPlaybooks, currentPlaybookIds]);

  // Apply search filter (case-insensitive name match)
  // Search applies to BOTH active and archived for predictable behavior
  const filteredPlaybooks = useMemo(() => {
    if (!searchQuery.trim()) {
      return linkablePlaybooks;
    }
    const query = searchQuery.toLowerCase().trim();
    return linkablePlaybooks.filter(p =>
      p.name.toLowerCase().includes(query)
    );
  }, [linkablePlaybooks, searchQuery]);

  // Group into active and archived
  const activePlaybooks = useMemo(
    () => sortByName(filteredPlaybooks.filter((p) => p.status === 'active')),
    [filteredPlaybooks]
  );

  const archivedPlaybooks = useMemo(
    () => sortByName(filteredPlaybooks.filter((p) => p.status === 'archived')),
    [filteredPlaybooks]
  );

  // Check if there are any linkable playbooks at all (before filtering)
  const hasLinkablePlaybooks = linkablePlaybooks.length > 0;

  /**
   * Toggle selection of a playbook
   */
  const handleToggleSelection = (playbookId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(playbookId)) {
        next.delete(playbookId);
      } else {
        next.add(playbookId);
      }
      return next;
    });
  };

  /**
   * Handle link button click
   */
  const handleLink = () => {
    if (selectedIds.size === 0) return;
    onLink(Array.from(selectedIds));
    onClose();
  };

  /**
   * Render a playbook item with checkbox
   */
  const renderPlaybookItem = (playbook: Playbook) => (
    <div
      key={playbook.id}
      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-default-100 cursor-pointer"
      onClick={() => handleToggleSelection(playbook.id)}
    >
      {/* Stop propagation to prevent double-toggle when clicking checkbox directly */}
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          isSelected={selectedIds.has(playbook.id)}
          onValueChange={() => handleToggleSelection(playbook.id)}
          aria-label={`Select ${playbook.name}`}
        />
      </div>
      <span className={`flex-1 truncate ${
        playbook.status === 'archived' ? 'text-text-tertiary' : 'text-text-primary'
      }`}>
        {playbook.name}
      </span>
      {playbook.status === 'archived' && (
        <span className="text-xs text-text-tertiary bg-default-100 px-1.5 py-0.5 rounded shrink-0">
          Archived
        </span>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex flex-col">
            <span>Link Existing Playbooks</span>
            <span className="text-sm font-normal text-text-secondary">
              Adding to: {strategyName}
            </span>
          </div>
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Search Input */}
          {hasLinkablePlaybooks && (
            <Input
              placeholder="Search playbooks..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={<Search size={16} className="text-text-tertiary" />}
              isClearable
              onClear={() => setSearchQuery('')}
              className="[&_input]:text-base"
            />
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <p className="text-text-secondary">Loading playbooks...</p>
            </div>
          )}

          {/* Error State */}
          {loadError && !loading && (
            <div className="space-y-2">
              <Alert color="danger" variant="flat">
                Failed to load playbooks. Please try again.
              </Alert>
              <Button size="sm" variant="flat" onPress={loadPlaybooks}>
                Retry
              </Button>
            </div>
          )}

          {/* Empty State - No linkable playbooks */}
          {!loading && !loadError && !hasLinkablePlaybooks && (
            <div className="text-center py-8 space-y-4">
              <p className="text-text-secondary">
                All playbooks are already linked to this strategy.
              </p>
              <Button
                variant='flat'
                color='primary'
                startContent={<Plus size={16} />}
                onPress={onCreateNew}
              >
                Create New Playbook
              </Button>
            </div>
          )}

          {/* Playbook List */}
          {!loading && !loadError && hasLinkablePlaybooks && (
            <div className="space-y-4">
              {/* Active Section */}
              {activePlaybooks.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-text-secondary">
                      Active ({activePlaybooks.length})
                    </h4>
                  </div>
                  <div className="space-y-1">
                    {activePlaybooks.map(renderPlaybookItem)}
                  </div>
                </div>
              )}

              {/* Show Archived Toggle */}
              <div className="flex items-center justify-between py-2 border-t border-default-200">
                <Button
                  size="sm"
                  variant="light"
                  startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setShowArchived(prev => !prev)}
                >
                  {showArchived ? 'Hide Archived' : 'Show Archived'}
                </Button>
                {showArchived && archivedPlaybooks.length > 0 && (
                  <span className="text-sm text-text-tertiary">
                    {archivedPlaybooks.length} archived
                  </span>
                )}
              </div>

              {/* Archived Section */}
              {showArchived && archivedPlaybooks.length > 0 && (
                <div>
                  <div className="space-y-1">
                    {archivedPlaybooks.map(renderPlaybookItem)}
                  </div>
                </div>
              )}

              {/* No results from search */}
              {filteredPlaybooks.length === 0 && searchQuery.trim() && (
                <div className="text-center py-4">
                  <p className="text-text-secondary text-sm">
                    No playbooks match "{searchQuery}"
                  </p>
                </div>
              )}

              {/* Hint when results are only in archived */}
              {activePlaybooks.length === 0 && archivedPlaybooks.length > 0 && !showArchived && (
                <div className="text-center py-4">
                  <p className="text-text-secondary text-sm">
                    {searchQuery.trim()
                      ? `Results are in archived playbooks`
                      : `All linkable playbooks are archived`}
                  </p>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleLink}
            isDisabled={selectedIds.size === 0}
          >
            Link Selected ({selectedIds.size})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LinkPlaybookModal;
